import {
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";

import { toast } from "react-toastify";

import { canMakePayment } from "../../../../utils/Permissions";

import {
  fetchGyngerCheckoutStatus,
  openPaymentSession,
} from "../../../../actions/usage";

const CHECKOUT_POLL_INTERVAL_MS = 3000;
const CHECKOUT_POLL_MAX_DURATION_MS = 15 * 60 * 1000;
const CHECKOUT_POLL_MAX_CONSECUTIVE_ERRORS = 5;
const CHECKOUT_FAILED_STATUSES = ["failed", "expired", "canceled", "cancelled", "declined"];
const CHECKOUT_STATUS_UNKNOWN_MESSAGE = "We couldn't confirm your payment status. Please check your billing before trying again.";
export const GYNGER_SUCCESS_MODAL_AUTO_CLOSE_MS = 3000;
export const GYNGER_CHECKOUT_PENDING_MESSAGE = "A payment tab is open. Complete your payment there — this window will update automatically.";
export const PAYMENT_PERMISSION_DENIED_MESSAGE = "You don't have permission to make payments.";

export default function useGyngerCheckoutPayment({
  onComplete,
} = {}) {
  const [submitting, setSubmitting] = useState(false);
  const [paymentPending, setPaymentPending] = useState(false);
  const [paymentUrl, setPaymentUrl] = useState("");
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [showFailedModal, setShowFailedModal] = useState(false);
  const pollIntervalRef = useRef(null);
  const pollAbortRef = useRef(null);
  const completedCheckoutResponseRef = useRef(null);
  const successCloseHandledRef = useRef(false);
  const canceledRef = useRef(false);

  const stopCheckoutPolling = useCallback(() => {
    if (pollIntervalRef.current) {
      clearInterval(pollIntervalRef.current);
      pollIntervalRef.current = null;
    }

    if (pollAbortRef.current) {
      pollAbortRef.current.abort();
      pollAbortRef.current = null;
    }
  }, []);

  const resetCheckout = useCallback(() => {
    canceledRef.current = true;
    stopCheckoutPolling();
    setSubmitting(false);
    setPaymentPending(false);
    setPaymentUrl("");
    setShowSuccessModal(false);
    setShowFailedModal(false);
    completedCheckoutResponseRef.current = null;
    successCloseHandledRef.current = false;
  }, [stopCheckoutPolling]);

  const handleCheckoutCompleted = useCallback((response) => {
    completedCheckoutResponseRef.current = response;
    setPaymentPending(false);
    setPaymentUrl("");
    setShowFailedModal(false);
    setShowSuccessModal(true);
  }, []);

  const handleCheckoutFailed = useCallback(() => {
    completedCheckoutResponseRef.current = null;
    setPaymentPending(false);
    setPaymentUrl("");
    setShowSuccessModal(false);
    setShowFailedModal(true);
  }, []);

  const handleCheckoutStatusUnknown = useCallback(() => {
    completedCheckoutResponseRef.current = null;
    setPaymentPending(false);
    setPaymentUrl("");
    toast.error(CHECKOUT_STATUS_UNKNOWN_MESSAGE);
  }, []);

  const startCheckoutPolling = useCallback((sessionId) => {
    stopCheckoutPolling();

    const controller = new AbortController();
    pollAbortRef.current = controller;
    setPaymentPending(true);

    const startedAt = Date.now();
    let consecutiveErrors = 0;

    const pollStatus = async () => {
      if (Date.now() - startedAt >= CHECKOUT_POLL_MAX_DURATION_MS) {
        stopCheckoutPolling();
        handleCheckoutStatusUnknown();
        return;
      }

      const response = await fetchGyngerCheckoutStatus(sessionId, controller.signal);

      if (canceledRef.current || response.canceled || controller.signal.aborted) {
        return;
      }

      if (!response.success) {
        consecutiveErrors += 1;
        const isClientError = response.statusCode >= 400
          && response.statusCode < 500
          && response.statusCode !== 401;
        if (isClientError || consecutiveErrors >= CHECKOUT_POLL_MAX_CONSECUTIVE_ERRORS) {
          stopCheckoutPolling();
          handleCheckoutStatusUnknown();
        }
        return;
      }

      consecutiveErrors = 0;
      const status = String(response.result?.status || "").toLowerCase();
      if (status === "completed") {
        stopCheckoutPolling();
        handleCheckoutCompleted(response);
        return;
      }

      if (CHECKOUT_FAILED_STATUSES.includes(status)) {
        stopCheckoutPolling();
        handleCheckoutFailed();
      }
    };

    pollStatus();
    pollIntervalRef.current = setInterval(pollStatus, CHECKOUT_POLL_INTERVAL_MS);
  }, [
    handleCheckoutCompleted,
    handleCheckoutFailed,
    handleCheckoutStatusUnknown,
    stopCheckoutPolling,
  ]);

  const handleSuccessClose = useCallback(async () => {
    if (successCloseHandledRef.current) {
      return;
    }

    successCloseHandledRef.current = true;
    const response = completedCheckoutResponseRef.current;
    completedCheckoutResponseRef.current = null;
    setShowSuccessModal(false);

    if (onComplete && response) {
      await onComplete(response);
    }
  }, [onComplete]);

  const handleFailedClose = useCallback(() => {
    setShowFailedModal(false);
  }, []);

  useEffect(() => () => {
    stopCheckoutPolling();
  }, [stopCheckoutPolling]);

  useEffect(() => {
    if (!showSuccessModal) {
      return undefined;
    }

    const timerId = setTimeout(() => {
      handleSuccessClose();
    }, GYNGER_SUCCESS_MODAL_AUTO_CLOSE_MS);

    return () => {
      clearTimeout(timerId);
    };
  }, [showSuccessModal, handleSuccessClose]);

  const startGyngerCheckout = useCallback(async ({
    amount,
    credits = [],
    purpose = "recharge",
  } = {}) => {
    if (!canMakePayment()) {
      toast.error(PAYMENT_PERMISSION_DENIED_MESSAGE);
      return false;
    }

    if (!Array.isArray(credits) || credits.length === 0) {
      toast.error("Select at least one test quantity to continue.");
      return false;
    }

    if (!(Number(amount) > 0)) {
      toast.error("Payment amount must be greater than $0.");
      return false;
    }

    canceledRef.current = false;
    setSubmitting(true);
    const response = await openPaymentSession({
      amount,
      credits,
      purpose,
    });
    setSubmitting(false);

    if (canceledRef.current || response.canceled) {
      return false;
    }

    const sessionId = response.session_id || response.id;
    if (!response.success || !sessionId || !response.session_url) {
      toast.error(response.message || "Unable to open payment session.");
      return false;
    }

    setPaymentUrl(response.session_url);
    window.open(
      response.session_url,
      "_blank",
      "noopener,noreferrer",
    );

    startCheckoutPolling(sessionId);
    return true;
  }, [startCheckoutPolling]);

  return {
    submitting,
    paymentPending,
    paymentUrl,
    isCheckoutLoading: submitting || paymentPending,
    showSuccessModal,
    handleSuccessClose,
    showFailedModal,
    handleFailedClose,
    startGyngerCheckout,
    resetCheckout,
  };
}
